"use client";

import {
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import {
    countryFlag,
    createCountryOptions,
    filterCountryOptions,
    ISO_COUNTRY_CODES,
    normalizeCountryCode,
} from "../countrySelectCore";
import type { CountryOption } from "../countrySelectCore";

type MorrowCountrySelectProps = {
    value: string;
    onChange: (countryCode: string) => void;
    label: string;
    locale: string;
    expandLabel: string;
    searchPlaceholder: string;
    noResults: string;
    required?: boolean;
    className?: string;
};

export const MORROW_COUNTRY_SELECT_CSS = `
    .morrow-country-select{display:flex;flex-direction:column;font-size:12px;font-weight:800;gap:7px;min-width:0;position:relative}
    .morrow-country-select-trigger{align-items:center;background:#fff;border:1px solid var(--site-border);border-radius:10px;color:var(--site-text);cursor:pointer;display:flex;font:inherit;gap:9px;min-height:44px;padding:7px 10px;text-align:left;width:100%}
    .morrow-country-select-trigger:focus-visible{border-color:var(--site-primary);outline:3px solid color-mix(in srgb,var(--site-primary) 12%,transparent)}
    .morrow-account-form-grid .morrow-country-select-trigger{background:var(--site-background);border-radius:12px;min-height:46px}
    .morrow-country-select-flag{align-items:center;background:var(--site-background);border:1px solid var(--site-border);border-radius:7px;display:flex;flex:0 0 auto;font-size:19px;height:28px;justify-content:center;line-height:1;width:36px}
    .morrow-country-select-name{font-size:13px;font-weight:750;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .morrow-country-select-code{background:var(--site-background);border-radius:6px;color:var(--site-muted);font-size:10px;font-weight:900;margin-left:auto;padding:5px 7px}
    .morrow-country-select-chevron{color:var(--site-muted);font-size:13px;transition:transform .16s ease}.morrow-country-select-chevron.is-open{transform:rotate(180deg)}
    .morrow-country-select-panel{background:var(--site-surface);border:1px solid var(--site-border);border-radius:14px;box-shadow:0 20px 52px var(--site-shadow-color);display:flex;flex-direction:column;overflow:hidden;position:fixed;z-index:1700}
    .morrow-country-select-search-wrap{border-bottom:1px solid var(--site-border);padding:10px}
    .morrow-country-select-search{background:var(--site-background);border:1px solid var(--site-border);border-radius:10px;color:var(--site-text);font:inherit;font-size:13px;min-height:42px;padding:9px 11px;width:100%}
    .morrow-country-select-search:focus{border-color:var(--site-primary);outline:3px solid color-mix(in srgb,var(--site-primary) 12%,transparent)}
    .morrow-country-select-options{min-height:0;overflow-y:auto;padding:6px}
    .morrow-country-select-option{align-items:center;background:transparent;border:0;border-radius:9px;color:var(--site-text);cursor:pointer;display:flex;font:inherit;gap:10px;min-height:42px;padding:7px 9px;text-align:left;width:100%}
    .morrow-country-select-option:hover,.morrow-country-select-option.is-active{background:var(--site-primary-soft)}
    .morrow-country-select-option.is-selected{color:var(--site-primary)}
    .morrow-country-select-option .morrow-country-select-name{flex:1}
    .morrow-country-select-check{font-size:16px;font-weight:900}
    .morrow-country-select-empty{color:var(--site-muted);font-size:12px;margin:0;padding:26px 12px;text-align:center}
    .morrow-country-select-visually-hidden{clip:rect(0,0,0,0);clip-path:inset(50%);height:1px;overflow:hidden;position:absolute;white-space:nowrap;width:1px}
`;

export function MorrowCountrySelect({
    value,
    onChange,
    label,
    locale,
    expandLabel,
    searchPlaceholder,
    noResults,
    required = false,
    className = "",
}: MorrowCountrySelectProps) {
    const controlId = useId();
    const listboxId = `${controlId}-listbox`;
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
    const [portalMounted, setPortalMounted] = useState(false);
    const normalizedValue = normalizeCountryCode(value);
    const options = useMemo(
        () => portalMounted
            ? createCountryOptions(ISO_COUNTRY_CODES, normalizedValue, locale)
            : [],
        [locale, normalizedValue, portalMounted],
    );
    const selectedOption = options.find((option) => option.code === normalizedValue) ?? null;
    const filteredOptions = useMemo(
        () => filterCountryOptions(options, search),
        [options, search],
    );
    const activeOption = filteredOptions[activeIndex] ?? null;

    useEffect(() => {
        setPortalMounted(true);
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        const ownerWindow = anchorRef.current?.ownerDocument.defaultView;

        if (!ownerWindow) {
            return;
        }

        const frame = ownerWindow.requestAnimationFrame(() => searchRef.current?.focus());

        return () => ownerWindow.cancelAnimationFrame(frame);
    }, [open]);

    useEffect(() => {
        if (!open || !activeOption) {
            return;
        }

        anchorRef.current?.ownerDocument
            .getElementById(`${listboxId}-${activeOption.code}`)
            ?.scrollIntoView({ block: "nearest" });
    }, [activeOption, listboxId, open]);

    useEffect(() => {
        const ownerDocument = anchorRef.current?.ownerDocument;
        const ownerWindow = ownerDocument?.defaultView;

        if (!ownerDocument || !ownerWindow) {
            return;
        }

        const OwnerNode = ownerWindow.Node;

        function closeOnOutsideClick(event: MouseEvent) {
            const target = event.target;

            if (!(target instanceof OwnerNode)) {
                return;
            }

            if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) {
                return;
            }

            setOpen(false);
            setSearch("");
            setActiveIndex(0);
        }

        ownerDocument.addEventListener("mousedown", closeOnOutsideClick);

        return () => ownerDocument.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            setPanelStyle(null);

            return;
        }

        function updatePanelPosition() {
            const anchor = anchorRef.current;

            if (!anchor) {
                return;
            }

            const ownerWindow = anchor.ownerDocument.defaultView;

            if (!ownerWindow) {
                return;
            }

            const rect = anchor.getBoundingClientRect();
            const viewportPadding = 14;
            const gap = 6;
            const spaceBelow = ownerWindow.innerHeight - rect.bottom - viewportPadding;
            const spaceAbove = rect.top - viewportPadding;
            const showAbove = spaceBelow < 280 && spaceAbove > spaceBelow;
            const availableHeight = showAbove ? spaceAbove : spaceBelow;
            const maxHeight = Math.min(370, Math.max(180, availableHeight - gap));
            const width = Math.min(
                Math.max(rect.width, 300),
                ownerWindow.innerWidth - viewportPadding * 2,
            );
            const left = Math.min(
                Math.max(viewportPadding, rect.left),
                ownerWindow.innerWidth - viewportPadding - width,
            );

            setPanelStyle({
                left,
                width,
                maxHeight,
                ...(showAbove
                    ? { bottom: ownerWindow.innerHeight - rect.top + gap }
                    : { top: rect.bottom + gap }),
            });
        }

        updatePanelPosition();
        const ownerWindow = anchorRef.current?.ownerDocument.defaultView;

        if (!ownerWindow) {
            return;
        }

        const frame = ownerWindow.requestAnimationFrame(updatePanelPosition);

        ownerWindow.addEventListener("resize", updatePanelPosition);
        ownerWindow.addEventListener("scroll", updatePanelPosition, true);

        return () => {
            ownerWindow.cancelAnimationFrame(frame);
            ownerWindow.removeEventListener("resize", updatePanelPosition);
            ownerWindow.removeEventListener("scroll", updatePanelPosition, true);
        };
    }, [open]);

    function close() {
        setOpen(false);
        setSearch("");
        setActiveIndex(0);
    }

    function openPicker() {
        const selectedIndex = options.findIndex((option) => option.code === normalizedValue);

        setSearch("");
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
    }

    function select(option: CountryOption) {
        onChange(option.code);
        close();
    }

    function moveActiveOption(direction: 1 | -1) {
        setActiveIndex((current) => {
            if (filteredOptions.length === 0) {
                return 0;
            }

            return (current + direction + filteredOptions.length) % filteredOptions.length;
        });
    }

    function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Escape") {
            event.preventDefault();
            close();
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            moveActiveOption(1);
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            moveActiveOption(-1);
        }

        if (event.key === "Enter" && activeOption) {
            event.preventDefault();
            select(activeOption);
        }
    }

    const panel = open && panelStyle ? (
        <div ref={panelRef} className="morrow-country-select-panel" style={panelStyle}>
            <div className="morrow-country-select-search-wrap">
                <input
                    ref={searchRef}
                    className="morrow-country-select-search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls={listboxId}
                    aria-expanded
                    aria-activedescendant={activeOption ? `${listboxId}-${activeOption.code}` : undefined}
                    aria-label={searchPlaceholder}
                    autoComplete="off"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                        setActiveIndex(0);
                    }}
                    onKeyDown={handleSearchKeyDown}
                />
            </div>
            <div id={listboxId} className="morrow-country-select-options" role="listbox" aria-label={label}>
                {filteredOptions.length > 0 ? filteredOptions.map((option, index) => {
                    const selected = option.code === normalizedValue;
                    const active = index === activeIndex;

                    return (
                        <button
                            id={`${listboxId}-${option.code}`}
                            key={option.code}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`morrow-country-select-option${selected ? " is-selected" : ""}${active ? " is-active" : ""}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => select(option)}
                        >
                            <span className="morrow-country-select-flag" aria-hidden>{option.flag}</span>
                            <span className="morrow-country-select-name">{option.name}</span>
                            {selected ? <span className="morrow-country-select-check" aria-hidden>✓</span> : null}
                        </button>
                    );
                }) : <p className="morrow-country-select-empty">{noResults}</p>}
            </div>
        </div>
    ) : null;
    const portalTarget = portalMounted
        ? anchorRef.current?.closest(".morrow-storefront")
            ?? anchorRef.current?.ownerDocument.body
            ?? null
        : null;

    return (
        <div className={`morrow-country-select ${className}`.trim()}>
            <span id={`${controlId}-label`}>{label}</span>
            <div ref={anchorRef}>
                <button
                    id={controlId}
                    type="button"
                    className="morrow-country-select-trigger"
                    aria-labelledby={`${controlId}-label`}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={open ? listboxId : undefined}
                    aria-required={required}
                    onClick={() => open ? close() : openPicker()}
                    onKeyDown={(event) => {
                        if (event.key === "ArrowDown") {
                            event.preventDefault();
                            openPicker();
                        }

                        if (event.key === "Escape" && open) {
                            event.preventDefault();
                            close();
                        }
                    }}
                >
                    {selectedOption ? (
                        <>
                            <span className="morrow-country-select-flag" aria-hidden>{selectedOption.flag}</span>
                            <span className="morrow-country-select-name">{selectedOption.name}</span>
                            <span className="morrow-country-select-code">{selectedOption.code}</span>
                        </>
                    ) : normalizedValue ? (
                        <>
                            <span className="morrow-country-select-flag" aria-hidden>{countryFlag(normalizedValue)}</span>
                            <span className="morrow-country-select-name">{normalizedValue}</span>
                            <span className="morrow-country-select-code">{normalizedValue}</span>
                        </>
                    ) : <span className="morrow-country-select-name">{label}</span>}
                    <span className={`morrow-country-select-chevron${open ? " is-open" : ""}`} aria-hidden>▾</span>
                    <span className="morrow-country-select-visually-hidden">{expandLabel}</span>
                </button>
            </div>
            <select
                className="morrow-country-select-visually-hidden"
                value={normalizedValue}
                required={required}
                tabIndex={-1}
                aria-hidden
                onChange={(event) => onChange(event.target.value)}
                onInvalid={(event) => {
                    event.preventDefault();
                    openPicker();
                    anchorRef.current?.querySelector("button")?.focus();
                }}
            >
                <option value="" />
                {portalMounted
                    ? options.map((option) => (
                        <option key={option.code} value={option.code}>{option.name}</option>
                    ))
                    : normalizedValue
                        ? <option value={normalizedValue}>{normalizedValue}</option>
                        : null}
            </select>
            {portalTarget && panel ? createPortal(panel, portalTarget) : null}
        </div>
    );
}
